import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import {
  AssistantToolCallStatus,
  type AssistantToolCallPart,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';

export function toolLabelKey(name: string): ParseKeys<'adminAssistant'> {
  return `tools.${name}` as ParseKeys<'adminAssistant'>;
}

interface ToolCallChipProps {
  part: AssistantToolCallPart;
}

export function ToolCallChip({ part }: ToolCallChipProps) {
  const { t, i18n } = useTranslation('adminAssistant');
  const labelKey = toolLabelKey(part.name);
  const label = i18n.exists(`adminAssistant:${labelKey}`)
    ? t(labelKey)
    : t('tools.fallback');
  const running = part.status === AssistantToolCallStatus.RUNNING;
  const failed = part.status === AssistantToolCallStatus.ERROR;
  const progress = part.progress;
  const percent =
    progress && progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : null;

  return (
    <div
      title={part.summary ?? undefined}
      className={cn(
        'bubble-in flex w-full flex-col gap-1.5 rounded-xl border px-3 py-2 text-xs',
        failed
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-border/50 bg-secondary/40 text-muted-foreground',
      )}
    >
      <div className="flex items-center gap-2">
        {running ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary-ink" />
        ) : failed ? (
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <Check className="h-3.5 w-3.5 shrink-0 text-primary-ink" />
        )}
        <span className="min-w-0 flex-1 truncate font-medium">
          {label}
          {running && progress?.label ? ` · ${progress.label}` : ''}
        </span>
        {running && percent !== null && (
          <span className="shrink-0 tabular-nums">{percent}%</span>
        )}
      </div>
      {running && percent !== null && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-background/60">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}
